import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';

import MetricOptionsContext from 'contexts/MetricOptionsContext';
import useMetric from 'hooks/useMetric';

import { getAvailablePlaceholders, parseSubtext } from './subtextPlaceholders';
import useStudioMetricValue from './useStudioMetricValue';

const Line = styled.div`
  color: var(--color-text-tertiary);
  font-size: 13px;
  line-height: 18px;
  overflow-wrap: anywhere;
`;

const MetricValue = styled.span`
  color: var(--color-text-secondary);
  font-weight: 500;
`;

// A placeholder's value, over the dates and filters the text sits in
const MetricValueText = ({ metricId }: { metricId: string }) => {
  const metric = useMetric(metricId);
  const { formattedValue, isLoading, isError } = useStudioMetricValue({
    metric,
  });
  return (
    <MetricValue>
      {isLoading ? '…' : isError ? '-' : formattedValue}
    </MetricValue>
  );
};

// A Number's subtext, with {placeholders} filled in from the block's metrics
// (see subtextPlaceholders.ts). Nothing when there's no text.
const NumberSubtext = ({
  text,
  metricIds,
  testId,
}: {
  text: string | undefined;
  // The block's metrics, which placeholders can name
  metricIds: string[];
  testId: string;
}) => {
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const parts = useMemo(
    () =>
      text && text.trim()
        ? parseSubtext({
            text,
            placeholders: getAvailablePlaceholders({
              metricIds,
              metricOptionsLookup,
            }),
          })
        : [],
    [metricIds, metricOptionsLookup, text],
  );

  if (parts.length === 0) {
    return null;
  }
  return (
    <Line data-testid={testId}>
      {parts.map((part, index) =>
        part.kind === 'text' ? (
          <React.Fragment key={index}>{part.text}</React.Fragment>
        ) : (
          <MetricValueText key={index} metricId={part.metricId} />
        ),
      )}
    </Line>
  );
};

export default NumberSubtext;
